import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Package, ArrowSquareOut } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { PublicShell } from "@/components/PublicShell";

export default function TrackPublic() {
  const { number } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useSeo({
    title: `Track ${number} | Unity & Utility`,
    description: "Follow a Unity & Utility consignment — carrier, status and the courier or post office tracking reference.",
    path: `/track/${number}`,
  });

  useEffect(() => {
    api.get(`/parcels/track/${number}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setError("Nothing is being tracked under that number yet."));
  }, [number]);

  return (
    <PublicShell>
      <div className="mx-auto max-w-2xl px-5 py-12 sm:px-8" data-testid="public-track-page">
        <p className="overline text-[#002FA7]">Consignment tracking</p>
        <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">{number}</h1>

        {error && <p data-testid="track-error" className="mt-6 border border-border bg-white p-5 text-sm text-slate-600">{error}</p>}

        {data && (
          <div className="mt-6 border border-border bg-white p-5" data-testid="track-card">
            <div className="flex items-start gap-3">
              <Package size={20} className="mt-0.5 text-[#002FA7]" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#0F172A]">{data.description}</div>
                <div className="mt-1 text-xs text-slate-500">
                  {[data.from_city, data.from_country].filter(Boolean).join(", ")} →{" "}
                  {[data.to_city, data.to_country].filter(Boolean).join(", ")}
                  {data.packages ? ` · ${data.packages} package(s)` : ""}
                </div>
              </div>
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              {[["Status", String(data.status || "").replace(/_/g, " ")], ["Carrier", data.carrier || "—"],
                ["Sent by", data.tracking_provider || "—"], ["ETA", data.eta || "—"]].map(([l, v]) => (
                <div key={l}>
                  <dt className="overline text-[0.6rem] text-slate-500">{l}</dt>
                  <dd className="mt-0.5 text-[#0F172A]">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 border-t border-border pt-4">
              <div className="overline text-[0.6rem] text-slate-500">Tracking reference</div>
              <div className="mt-1 font-mono text-sm text-[#0F172A]" data-testid="track-reference">{data.tracking_ref}</div>
              {data.tracking_url && (
                <a href={data.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="track-external-link" className="mt-3 inline-flex items-center gap-1.5 bg-[#002FA7] px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#00248A]">
                  Follow it on {data.tracking_provider} <ArrowSquareOut size={14} />
                </a>
              )}
            </div>
          </div>
        )}

        <Link to="/feed" className="mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
          See what else is happening at Unity &amp; Utility
        </Link>
      </div>
    </PublicShell>
  );
}
